/**
 * OptimierungenDrawer — Container des Optimierungen-Drawers (Claude-Design drawer-v2).
 * Pro-Feld-Modell: jede Zeile einzeln auslösbar, dezenter Sammelknopf. Kein Alles-oder-Nichts.
 * Apply/Export via bestehendem /descriptions/push; Status pro Feld (rest/busy/suggestion/applied/error).
 */
import { Sparkles } from 'lucide-react'

import { OptimierungDrawerShell } from './OptimierungDrawerShell'
import { OptimierungFieldItem } from './OptimierungFieldItem'
import type { OptimierungField } from './optimierungenTypes'
import { DV2 } from './tokens'
import { useOptimierungen } from './useOptimierungen'

export interface OptimierungenDrawerProps {
  open: boolean
  productId: string
  productName: string
  channelId: string
  salesChannelId: string
  channelName: string
  channelHost?: string
  channelLanguage?: string
  creditBalance?: string
  initialFields: OptimierungField[]
  onClose: () => void
  onConfigureChannel?: () => void
}

export function OptimierungenDrawer({
  open,
  productId,
  productName,
  channelId,
  salesChannelId,
  channelName,
  channelHost,
  channelLanguage,
  creditBalance,
  initialFields,
  onClose,
  onConfigureChannel,
}: OptimierungenDrawerProps) {
  const { fields, optimize, optimizeBulk, apply, discard, undo } = useOptimierungen(
    productId,
    channelId,
    salesChannelId,
    initialFields,
  )

  if (!open) return null

  const toOptimize = fields.filter((f) => !f.proactive && (f.status === 'rest' || f.status === 'error'))
  const proactiveCount = fields.filter((f) => f.proactive).length
  const bulkCost = toOptimize.reduce((sum, f) => sum + f.cost, 0)
  const busy = fields.some((f) => f.status === 'busy')
  const rowFields = fields.filter((f) => f.status === 'rest' || f.status === 'busy')
  const cardFields = fields.filter((f) => f.status === 'suggestion' || f.status === 'applied' || f.status === 'error')

  const item = (f: OptimierungField) => (
    <OptimierungFieldItem
      key={f.attributeCode}
      field={f}
      channelName={channelName}
      onOptimize={optimize}
      onApply={apply}
      onDiscard={discard}
      onConfigure={onConfigureChannel}
      onUndo={undo}
    />
  )

  return (
    <OptimierungDrawerShell
      productName={productName}
      channelName={channelName}
      channelHost={channelHost}
      channelLanguage={channelLanguage}
      creditBalance={creditBalance}
      channelSub={
        <>
          <b>{fields.length} Felder</b> · {toOptimize.length} zu optimieren · {proactiveCount} proaktiv
        </>
      }
      footerMeta={<>Pro Feld eine eigene Entscheidung · Du bezahlst nur, was du auslöst.</>}
      onClose={onClose}
    >
      <div>
        <div className={`text-lg font-semibold leading-snug ${DV2.fg1}`}>
          Diese Felder kannst du für <span className={DV2.orange}>{channelName}</span> optimieren.
        </div>
        <p className={`mt-1.5 text-xs ${DV2.fg3}`}>
          GOKI schreibt regelkonforme Texte aus deinen Produktdaten — pro Feld entscheidest du selbst.
        </p>
      </div>

      {rowFields.length > 0 && (
        <div className={`overflow-hidden rounded-xl border ${DV2.line} ${DV2.card}`}>
          <div className="flex items-center justify-between gap-2 px-3.5 py-2.5">
            <span className={`text-xs-plus ${DV2.fg4}`}>
              <b className={DV2.fg2}>{toOptimize.length}</b> zu optimieren · <b className={DV2.fg2}>{proactiveCount}</b> proaktiv
            </span>
            {toOptimize.length >= 2 && (
              <button
                type="button"
                onClick={optimizeBulk}
                disabled={busy}
                title="Alle Felder mit Handlungsbedarf in einem Lauf"
                className={`flex items-center gap-1.5 rounded-md border ${DV2.line} px-2.5 py-1.5 text-xs-plus font-semibold ${DV2.fg2} disabled:opacity-50`}
              >
                <Sparkles size={11} className="text-primary-500" />
                Alle {toOptimize.length} optimieren
                <span className={`font-mono ${DV2.fg4}`}>{bulkCost} Cr</span>
              </button>
            )}
          </div>
          {rowFields.map(item)}
        </div>
      )}

      {cardFields.map(item)}
    </OptimierungDrawerShell>
  )
}
